import { useEffect, useState } from 'react'
import axios from 'axios'
import { SettingCard, Skeleton, Spinner, StatusBadge, Switch, Toast, ToastTone } from '../_ds/Primitives'

type OnboardingVersion = 'v2' | 'v3'

interface ToastState {
    open: boolean
    message: string
    tone: ToastTone
}

export default function SystemConfig() {
    const [defaultVersion, setDefaultVersion] = useState<OnboardingVersion>('v2')
    const [loadboardBannerEnabled, setLoadboardBannerEnabled] = useState(true)
    const [promoThreeMonthsEnabled, setPromoThreeMonthsEnabled] = useState(false)
    const [loading, setLoading] = useState(true)
    const [savingKey, setSavingKey] = useState<string | null>(null)
    const [toast, setToast] = useState<ToastState>({ open: false, message: '', tone: 'success' })

    useEffect(() => {
        void fetchConfig()
    }, [])

    const fetchConfig = async (): Promise<void> => {
        try {
            const [onboardingRes, bannerRes, promoRes] = await Promise.all([
                axios.get<{ success: boolean; data: { defaultVersion: OnboardingVersion } }>('/gluon/config/onboarding'),
                axios.get<{ success: boolean; data: { enabled: boolean } }>('/gluon/config/loadboard-banner'),
                axios.get<{ success: boolean; data: { enabled: boolean } }>('/gluon/config/promo-three-months'),
            ])
            if (onboardingRes.data.success) setDefaultVersion(onboardingRes.data.data.defaultVersion)
            if (bannerRes.data.success) setLoadboardBannerEnabled(bannerRes.data.data.enabled)
            if (promoRes.data.success) setPromoThreeMonthsEnabled(promoRes.data.data.enabled)
        } catch {
            setToast({ open: true, message: 'Failed to load system config', tone: 'danger' })
        } finally {
            setLoading(false)
        }
    }

    const handleVersionChange = async (value: OnboardingVersion): Promise<void> => {
        if (value === defaultVersion) return
        setSavingKey('onboarding')
        try {
            const response = await axios.put<{ success: boolean; data: { defaultVersion: OnboardingVersion } }>('/gluon/config/onboarding', {
                defaultVersion: value,
            })
            if (response.data.success) {
                setDefaultVersion(value)
                setToast({ open: true, message: 'Onboarding version updated successfully', tone: 'success' })
            }
        } catch {
            setToast({ open: true, message: 'Failed to update onboarding version', tone: 'danger' })
        } finally {
            setSavingKey(null)
        }
    }

    const handleBannerToggle = async (): Promise<void> => {
        const newValue = !loadboardBannerEnabled
        setSavingKey('banner')
        try {
            const response = await axios.put<{ success: boolean; data: { enabled: boolean } }>('/gluon/config/loadboard-banner', { enabled: newValue })
            if (response.data.success) {
                setLoadboardBannerEnabled(newValue)
                setToast({ open: true, message: `Loadboard banner ${newValue ? 'enabled' : 'disabled'}`, tone: 'success' })
            }
        } catch {
            setToast({ open: true, message: 'Failed to update loadboard banner setting', tone: 'danger' })
        } finally {
            setSavingKey(null)
        }
    }

    const handlePromoThreeMonthsToggle = async (): Promise<void> => {
        const newValue = !promoThreeMonthsEnabled
        setSavingKey('promo')
        try {
            const response = await axios.put<{ success: boolean; data: { enabled: boolean } }>('/gluon/config/promo-three-months', { enabled: newValue })
            if (response.data.success) {
                setPromoThreeMonthsEnabled(newValue)
                setToast({ open: true, message: `"Get 3 Months for $0" promo ${newValue ? 'enabled' : 'disabled'}`, tone: 'success' })
            }
        } catch {
            setToast({ open: true, message: 'Failed to update promo setting', tone: 'danger' })
        } finally {
            setSavingKey(null)
        }
    }

    return (
        <div className="mx-auto w-full max-w-3xl px-6 py-8">
            <header className="mb-6">
                <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">System Configuration</h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Controls that affect every Numeo extension install.</p>
            </header>

            {loading ? (
                <LoadingStack />
            ) : (
                <div className="space-y-6">
                    <SettingCard
                        title="Onboarding Flow"
                        description="Controls which onboarding flow new extension installs see. v2 is the legacy flow (/ext-v2), v3 is the new Numeo Spot onboarding (/onboarding)."
                        status={
                            <StatusBadge tone="info" dot>
                                {defaultVersion === 'v3' ? 'v3 active' : 'v2 active'}
                            </StatusBadge>
                        }
                    >
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-slate-900 dark:text-slate-100" htmlFor="onboarding-select">
                                Default flow
                            </label>
                            <select
                                id="onboarding-select"
                                value={defaultVersion}
                                onChange={(e) => void handleVersionChange(e.target.value as OnboardingVersion)}
                                disabled={savingKey === 'onboarding'}
                                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                            >
                                <option value="v2">v2 (Legacy)</option>
                                <option value="v3">v3 (Numeo Spot)</option>
                            </select>
                            {savingKey === 'onboarding' && <Spinner />}
                        </div>
                    </SettingCard>

                    <SettingCard
                        title="Loadboard Banner"
                        description='Controls the "See 15+ loadboards" banner shown in the DAT header on one.dat.com. When disabled, the banner and its settings toggle are hidden for all users.'
                        status={
                            <StatusBadge tone={loadboardBannerEnabled ? 'on' : 'off'} dot>
                                {loadboardBannerEnabled ? 'ON' : 'OFF'}
                            </StatusBadge>
                        }
                    >
                        <ToggleRow
                            label="Show loadboard banner"
                            checked={loadboardBannerEnabled}
                            onChange={() => void handleBannerToggle()}
                            saving={savingKey === 'banner'}
                        />
                    </SettingCard>

                    <SettingCard
                        title='"Get 3 Months for $0" Promo'
                        description='Controls the "Get 3 Months for $0" promotional button shown on the DAT extension toolbar (next to "See 15+ loadboards"). When disabled, the button is hidden for all users. Per-user eligibility is FREE plan only — PRO/Ultra users never see it regardless of this toggle.'
                        status={
                            <StatusBadge tone={promoThreeMonthsEnabled ? 'on' : 'off'} dot>
                                {promoThreeMonthsEnabled ? 'ON' : 'OFF'}
                            </StatusBadge>
                        }
                    >
                        <ToggleRow
                            label="Show promo button"
                            checked={promoThreeMonthsEnabled}
                            onChange={() => void handlePromoThreeMonthsToggle()}
                            saving={savingKey === 'promo'}
                        />
                    </SettingCard>
                </div>
            )}

            <Toast open={toast.open} tone={toast.tone} message={toast.message} onClose={() => setToast((prev) => ({ ...prev, open: false }))} />
        </div>
    )
}

// ─── helpers ────────────────────────────────────────────────────────────────

function ToggleRow({
    label,
    description,
    checked,
    onChange,
    saving,
}: {
    label: string
    description?: string
    checked: boolean
    onChange: () => void
    saving: boolean
}) {
    return (
        <div className="flex items-center justify-between gap-6">
            <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{label}</p>
                {description && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{description}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
                {saving && <Spinner />}
                <Switch checked={checked} onChange={onChange} disabled={saving} ariaLabel={label} />
            </div>
        </div>
    )
}

function LoadingStack() {
    return (
        <div className="space-y-6">
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                    <Skeleton className="mb-3 h-5 w-40" />
                    <Skeleton className="mb-2 h-3 w-full max-w-lg" />
                    <Skeleton className="mb-6 h-3 w-2/3 max-w-lg" />
                    <Skeleton className="h-9 w-32" />
                </div>
            ))}
        </div>
    )
}
